import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Checkbox, ConfirmDialog, Field, Input, LinkButton, Select, Textarea } from '../components/ui'
import { useAvailableImages } from '../hooks/useImages'
import {
  useTemplatesAll,
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useDeactivateTemplate,
  useDeleteTemplate,
  useAddTemplateModule,
  useRemoveTemplateModule,
  useSetDefault,
  useValidateTemplate,
  useTemplateUsage,
  useFreezeTemplate,
} from '../hooks/useTemplates'
import type { ModuleTemplate, TemplateValidationResult } from '../api/client'

type TemplateForm = {
  name: string
  description: string
  is_active: boolean
  /** "" = any Odoo series */
  odoo_version: string
}

type ModuleForm = {
  source: 'github' | 'builtin'
  github_url: string
  branch: string
  subdirectory: string
  module_name: string
  description: string
}

const emptyTemplateForm: TemplateForm = { name: '', description: '', is_active: true, odoo_version: '' }
const emptyModuleForm: ModuleForm = { source: 'github', github_url: '', branch: 'main', subdirectory: '', module_name: '', description: '' }

const GITHUB_URL_REGEX = /^https:\/\/github\.com\/[^/]+\/[^/]+\/?$/

function TemplateUsageCell({ templateId }: { templateId: string }) {
  const { t } = useTranslation()
  const { data, isLoading } = useTemplateUsage(templateId)
  if (isLoading) return <span className="text-xs text-gray-400">…</span>
  const count = data?.count ?? 0
  return (
    <span className="text-xs text-gray-500">
      {t('templates.usageCount', { count })}
    </span>
  )
}

export default function TemplatesPage() {
  const { t } = useTranslation()
  const { data: templates = [], isLoading } = useTemplatesAll()
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()
  const deactivateMutation = useDeactivateTemplate()
  const deleteMutation = useDeleteTemplate()
  const addModuleMutation = useAddTemplateModule()
  const removeModuleMutation = useRemoveTemplateModule()
  const setDefaultMutation = useSetDefault()
  const validateMutation = useValidateTemplate()
  const freezeMutation = useFreezeTemplate()
  const [freezeResult, setFreezeResult] = useState<{ frozen: string[]; missing: string[] } | null>(null)
  const available = useAvailableImages()
  const odooSeries = Array.from(new Set((available.data ?? []).filter((i) => i.kind === 'odoo').map((i) => i.series)))

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<ModuleTemplate | null>(null)
  const [form, setForm] = useState<TemplateForm>(emptyTemplateForm)
  const [formError, setFormError] = useState('')

  // Detail / module management
  const [detailId, setDetailId] = useState<string | null>(null)
  const [showModuleForm, setShowModuleForm] = useState(false)
  const [moduleForm, setModuleForm] = useState<ModuleForm>(emptyModuleForm)
  const [moduleError, setModuleError] = useState('')
  // Row actions used to await mutateAsync with no catch: a failed request was
  // invisible to the user AND surfaced as an unhandled rejection, which vitest
  // can attribute to an unrelated test file.
  const [actionError, setActionError] = useState('')
  const [validationResults, setValidationResults] = useState<TemplateValidationResult[] | null>(null)
  // Native confirm() replaced by ConfirmDialog — the browser dialog cannot be
  // styled and never went through i18n.
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null)

  const { data: detailTemplate } = useTemplate(detailId ?? '')

  const openCreate = () => {
    setEditing(null)
    setForm(emptyTemplateForm)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (tmpl: ModuleTemplate) => {
    setEditing(tmpl)
    setForm({ name: tmpl.name, description: tmpl.description ?? '', is_active: tmpl.is_active, odoo_version: tmpl.odoo_version ?? '' })
    setFormError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, body: form })
      } else {
        await createMutation.mutateAsync(form)
      }
      setShowForm(false)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setFormError(msg ?? t('templates.operationFailed'))
    }
  }

  // mutate() rather than mutateAsync(): React Query keeps the rejection in
  // mutation state instead of handing back a promise nobody awaits.
  const onActionError = (err: unknown) => {
    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
    setActionError(msg ?? t('templates.operationFailed'))
  }

  const handleDeactivate = (id: string) => {
    setActionError('')
    deactivateMutation.mutate(id, { onError: onActionError })
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    setActionError('')
    // On failure the dialog stays open with the reason in it, so the admin can
    // read it and retry rather than watching nothing happen.
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: onActionError,
    })
  }

  const handleActivate = (id: string) => {
    setActionError('')
    updateMutation.mutate({ id, body: { is_active: true } }, { onError: onActionError })
  }

  const handleSetDefault = (id: string) => {
    setActionError('')
    setDefaultMutation.mutate(id, { onError: onActionError })
  }

  const isGithubSource = moduleForm.source === 'github'
  const githubUrlValid = !isGithubSource || GITHUB_URL_REGEX.test(moduleForm.github_url.trim())

  const handleAddModule = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!detailId) return
    setModuleError('')
    if (isGithubSource && !githubUrlValid) {
      setModuleError(t('templates.invalidGithubUrl'))
      return
    }
    try {
      await addModuleMutation.mutateAsync({
        id: detailId,
        body: isGithubSource
          ? {
              source: 'github',
              github_url: moduleForm.github_url.trim(),
              branch: moduleForm.branch || 'main',
              subdirectory: moduleForm.subdirectory || undefined,
              module_name: moduleForm.module_name,
              description: moduleForm.description || undefined,
            }
          : {
              source: 'builtin',
              module_name: moduleForm.module_name,
              description: moduleForm.description || undefined,
            },
      })
      setModuleForm(emptyModuleForm)
      setShowModuleForm(false)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setModuleError(msg ?? t('templates.operationFailed'))
    }
  }

  const handleRemoveModule = (moduleId: string) => {
    if (!detailId) return
    setActionError('')
    removeModuleMutation.mutate({ templateId: detailId, moduleId }, { onError: onActionError })
  }

  const handleValidate = async () => {
    if (!detailId) return
    try {
      const results = await validateMutation.mutateAsync(detailId)
      setValidationResults(results)
    } catch {
      setValidationResults([])
    }
  }

  const handleFreeze = () => {
    if (!detailId) return
    setActionError('')
    setFreezeResult(null)
    freezeMutation.mutate(detailId, { onSuccess: setFreezeResult, onError: onActionError })
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  // Detail view
  if (detailId && detailTemplate) {
    const resultByName = new Map<string, TemplateValidationResult>()
    ;(validationResults ?? []).forEach((r) => resultByName.set(r.module_name, r))

    return (
      <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
        <LinkButton
          onClick={() => { setDetailId(null); setShowModuleForm(false); setValidationResults(null) }}
          className="text-sm mb-4 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('templates.backToList')}
        </LinkButton>

        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{detailTemplate.name}</h1>
              {detailTemplate.is_default && (
                <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded font-medium">
                  {t('templates.default')}
                </span>
              )}
              <Badge tone="neutral">
                {detailTemplate.odoo_version ? `Odoo ${detailTemplate.odoo_version}` : t('templates.anyVersion')}
              </Badge>
            </div>
            {detailTemplate.description && (
              <p className="text-sm text-gray-500 mt-0.5">{detailTemplate.description}</p>
            )}
            <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${detailTemplate.is_active ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
              {detailTemplate.is_active ? t('templates.active') : t('templates.inactive')}
            </span>
          </div>
          <div className="flex gap-2 shrink-0">
            {(detailTemplate.modules ?? []).some((m) => m.source === 'github') && (
              <Button variant="secondary" onClick={handleFreeze} loading={freezeMutation.isPending}>
                {t('templates.freeze')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleValidate}
              loading={validateMutation.isPending}
              disabled={(detailTemplate.modules ?? []).length === 0}
            >
              {validateMutation.isPending ? t('templates.validating') : t('templates.validateModules')}
            </Button>
            <Button onClick={() => setShowModuleForm(true)}>{t('templates.addModule')}</Button>
          </div>
        </div>

        {actionError && <p className="text-sm text-red-600 mb-4">{actionError}</p>}
        {freezeResult && (
          <p className="text-sm text-gray-700 mb-4" role="status">
            {t('templates.frozenCount', { count: freezeResult.frozen.length })}
            {freezeResult.missing.length > 0 && (
              <span className="text-red-600"> {t('templates.freezeMissing', { names: freezeResult.missing.join(', ') })}</span>
            )}
          </p>
        )}

        {showModuleForm && (
          <form onSubmit={handleAddModule} className="bg-white rounded-xl border border-gray-200 p-6 mb-6 space-y-4">
            <h2 className="font-semibold text-gray-800">{t('templates.addModule')}</h2>
            {moduleError && <p className="text-sm text-red-600">{moduleError}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelSource')}</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="module-source"
                    value="github"
                    checked={moduleForm.source === 'github'}
                    onChange={() => setModuleForm({ ...moduleForm, source: 'github' })}
                  />
                  {t('templates.sourceGithub')}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="module-source"
                    value="builtin"
                    checked={moduleForm.source === 'builtin'}
                    onChange={() => setModuleForm({ ...moduleForm, source: 'builtin' })}
                  />
                  {t('templates.sourceBuiltin')}
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isGithubSource && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelGithubUrl')} *</label>
                  <input
                    type="text"
                    required
                    value={moduleForm.github_url}
                    onChange={(e) => setModuleForm({ ...moduleForm, github_url: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                      moduleForm.github_url && !githubUrlValid
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-brand-500'
                    }`}
                    placeholder="https://github.com/owner/repo"
                  />
                  {moduleForm.github_url && !githubUrlValid && (
                    <p className="text-xs text-red-600 mt-1">{t('templates.invalidGithubUrl')}</p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelModuleName')} *</label>
                <input
                  type="text"
                  required
                  value={moduleForm.module_name}
                  onChange={(e) => setModuleForm({ ...moduleForm, module_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={isGithubSource ? t('templates.moduleNamePlaceholderGithub') : t('templates.moduleNamePlaceholderBuiltin')}
                />
              </div>
              {isGithubSource && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelBranch')}</label>
                    <input
                      type="text"
                      value={moduleForm.branch}
                      onChange={(e) => setModuleForm({ ...moduleForm, branch: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="main"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelSubdirectory')}</label>
                    <input
                      type="text"
                      value={moduleForm.subdirectory}
                      onChange={(e) => setModuleForm({ ...moduleForm, subdirectory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="addons/my_module"
                    />
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('templates.labelDescription')}</label>
                <input
                  type="text"
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={t('templates.moduleDescPlaceholder')}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => { setShowModuleForm(false); setModuleError('') }}
              >
                {t('templates.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={addModuleMutation.isPending}
                disabled={!githubUrlValid || !moduleForm.module_name}
              >
                {addModuleMutation.isPending ? t('templates.saving') : t('templates.addModuleBtn')}
              </Button>
            </div>
          </form>
        )}

        {/* Module list */}
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {(detailTemplate.modules ?? []).length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500">{t('templates.noModules')}</div>
          ) : (
            detailTemplate.modules!.map((mod) => {
              const res = resultByName.get(mod.module_name)
              return (
                <div key={mod.id} className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {res && (
                        <span
                          title={res.error ?? ''}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                            res.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {res.ok ? '✓' : '✗'}
                        </span>
                      )}
                      <p className="font-medium text-gray-900 text-sm">{mod.module_name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        mod.source === 'builtin' ? 'bg-purple-100 text-purple-700'
                        : mod.source === 'zip' ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                      }`}>
                        {mod.source}
                      </span>
                      {mod.source === 'github' && mod.branch && (
                        <span className="text-xs text-gray-400">· {mod.branch}</span>
                      )}
                    </div>
                    {mod.source === 'github' && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{mod.github_url}</p>
                    )}
                    {mod.source === 'zip' && mod.archive_sha && (
                      <p className="text-xs text-gray-400 mt-0.5 font-mono truncate">{mod.archive_sha.slice(0, 12)}…</p>
                    )}
                    {mod.subdirectory && (
                      <p className="text-xs text-gray-400 mt-0.5">/{mod.subdirectory}</p>
                    )}
                    {mod.description && (
                      <p className="text-xs text-gray-400 mt-0.5">{mod.description}</p>
                    )}
                    {res && !res.ok && res.error && (
                      <p className="text-xs text-red-600 mt-0.5">{res.error}</p>
                    )}
                  </div>
                  <LinkButton
                    tone="danger"
                    onClick={() => handleRemoveModule(mod.id)}
                    disabled={removeModuleMutation.isPending}
                    className="shrink-0"
                  >
                    {t('templates.remove')}
                  </LinkButton>
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  // List view
  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('templates.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('templates.subtitle')}</p>
        </div>
        <Button onClick={openCreate}>
          {t('templates.add')}
        </Button>
      </div>

      {actionError && <p className="text-sm text-red-600 mb-4">{actionError}</p>}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 mb-6 space-y-4">
          <h2 className="font-semibold text-gray-800">{editing ? t('templates.formTitleEdit') : t('templates.formTitleNew')}</h2>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('templates.labelName')} required>
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t('templates.namePlaceholder')}
                />
              )}
            </Field>
            <div className="flex items-end pb-2">
              <Checkbox
                label={t('templates.labelActive')}
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            </div>
            <Field label={t('templates.labelVersion')} hint={t('templates.versionHint')}>
              {(p) => (
                <Select {...p} value={form.odoo_version} onChange={(e) => setForm({ ...form, odoo_version: e.target.value })}>
                  <option value="">{t('templates.anyVersion')}</option>
                  {odooSeries.map((v) => <option key={v} value={v}>Odoo {v}</option>)}
                  {form.odoo_version && !odooSeries.includes(form.odoo_version) && (
                    <option value={form.odoo_version}>Odoo {form.odoo_version}</option>
                  )}
                </Select>
              )}
            </Field>
            <Field label={t('templates.labelDescription')} className="sm:col-span-2">
              {(p) => (
                <Textarea
                  {...p}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder={t('templates.descPlaceholder')}
                />
              )}
            </Field>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setShowForm(false)}>
              {t('templates.cancel')}
            </Button>
            <Button type="submit" className="flex-1" loading={isPending}>
              {isPending ? t('templates.saving') : (editing ? t('templates.saveChanges') : t('templates.createBtn'))}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {templates.map((tmpl) => (
            <div key={tmpl.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setDetailId(tmpl.id)}
                    className="font-medium text-gray-900 hover:text-brand-600 transition-colors"
                  >
                    {tmpl.name}
                  </button>
                  {tmpl.is_default && (
                    <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded font-medium">
                      {t('templates.default')}
                    </span>
                  )}
                  {!tmpl.is_active && (
                    <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">{t('templates.inactive')}</span>
                  )}
                  {tmpl.odoo_version && <Badge tone="neutral">Odoo {tmpl.odoo_version}</Badge>}
                </div>
                {tmpl.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{tmpl.description}</p>
                )}
                <div className="flex items-center gap-3 mt-0.5">
                  <p className="text-xs text-gray-400">
                    {(tmpl.modules?.length ?? 0)} {t('templates.moduleCount')}
                  </p>
                  <TemplateUsageCell templateId={tmpl.id} />
                </div>
              </div>
              <div className="flex items-center gap-x-3 gap-y-2 flex-wrap sm:shrink-0 sm:justify-end">
                {tmpl.is_active && !tmpl.is_default && (
                  <LinkButton onClick={() => handleSetDefault(tmpl.id)} disabled={setDefaultMutation.isPending}>
                    {t('templates.setDefault')}
                  </LinkButton>
                )}
                <LinkButton onClick={() => setDetailId(tmpl.id)}>{t('templates.manage')}</LinkButton>
                <LinkButton onClick={() => openEdit(tmpl)}>{t('templates.edit')}</LinkButton>
                {tmpl.is_active ? (
                  <LinkButton tone="danger" onClick={() => handleDeactivate(tmpl.id)} disabled={deactivateMutation.isPending}>
                    {t('templates.deactivate')}
                  </LinkButton>
                ) : (
                  <LinkButton tone="success" onClick={() => handleActivate(tmpl.id)} disabled={updateMutation.isPending}>
                    {t('templates.activate')}
                  </LinkButton>
                )}
                <LinkButton
                  tone="danger"
                  onClick={() => setPendingDelete({ id: tmpl.id, name: tmpl.name })}
                  disabled={deleteMutation.isPending}
                >
                  {t('templates.delete')}
                </LinkButton>
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-500">{t('templates.empty')}</div>
          )}
        </div>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('templates.delete')}
          message={
            <>
              {t('templates.deleteConfirm', { name: pendingDelete.name })}
              {actionError && <span className="mt-2 block text-red-600">{actionError}</span>}
            </>
          }
          confirmLabel={t('templates.delete')}
          busy={deleteMutation.isPending}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  )
}
